import { TFunction } from 'i18next';

import { JourneyProgressResponse } from '@/features/service-tasks/api/tasks.service';

export type JourneyItemType = 'checkbox' | 'description' | 'radio' | 'accept-reject';

export interface JourneySubStep {
  id: string;
  label: string;
  rejectedLabel?: string;
  acceptSubStepId?: string;
  type?: JourneyItemType;
  options?: string[];
  indented?: boolean;
}

export type JourneyActionSource = 'api' | 'manual';

export type EmailTemplateKind =
  | 'print-supplier'
  | 'location-decoration'
  | 'supplier-decoration'
  | 'supplier-order-update'
  | 'location-installation'
  | 'location-neutralization'
  | 'supplier-neutralization';

export type FileSectionKind =
  | 'artwork-previews'
  | 'support-pictures'
  | 'layout-sketches'
  | 'after-decoration'
  | 'simulations';

export interface JourneyEmailTemplate {
  kind: EmailTemplateKind;
  fileSections: FileSectionKind[];
}

export interface JourneyActionConfig {
  id: string;
  title: string;
  source: JourneyActionSource;
  subSteps: JourneySubStep[];
  emailTemplate?: JourneyEmailTemplate;
}

export interface JourneyStepConfig {
  id: string;
  title: string;
  actions: JourneyActionConfig[];
}

const LOCATION_EMAIL_BODY_ITEMS: JourneySubStep[] = [
  { id: 'date-and-time', label: 'Date and Time', type: 'description', indented: true },
  { id: 'location', label: 'Location', type: 'description', indented: true },
  { id: 'indoor', label: 'Indoor', type: 'description', indented: true },
  { id: 'outdoor', label: 'Outdoor', type: 'description', indented: true },
  { id: 'device-name', label: 'Device name', type: 'description', indented: true },
  { id: 'height-permits', label: 'Height Permits', type: 'description', indented: true },
  {
    id: 'ssm-industrial-climber',
    label: 'SSM Industrial climber',
    type: 'description',
    indented: true,
  },
  {
    id: 'mewp-specifications',
    label: 'Mobile elevating work platform Specifications',
    type: 'description',
    indented: true,
  },
  {
    id: 'mewp-iscir',
    label: 'Mobile elevating work platform ISCIR',
    type: 'description',
    indented: true,
  },
  {
    id: 'mewp-operator',
    label: 'Mobile elevating work platform Operator',
    type: 'description',
    indented: true,
  },
  {
    id: 'vehicle-plate-id',
    label: 'Vehicle licence plate ID',
    type: 'description',
    indented: true,
  },
  {
    id: 'location-security-phone',
    label: 'Location security/dispatch telephone number for access',
    type: 'description',
    indented: true,
  },
  {
    id: 'estimated-duration',
    label: 'Estimated duration of the operation',
    type: 'description',
    indented: true,
  },
  {
    id: 'team-document-id',
    label: 'Team identity document ID',
    type: 'description',
    indented: true,
  },
  { id: 'material', label: 'Material', type: 'description', indented: true },
  { id: 'dimensions', label: 'Dimensions', type: 'description', indented: true },
  { id: 'artwork-preview', label: 'Artwork Preview', type: 'description', indented: true },
  {
    id: 'illumination-requirements',
    label: 'Illumination requirements',
    type: 'description',
    indented: true,
  },
  { id: 'power-supply', label: 'Power supply required', type: 'description', indented: true },
  { id: 'mall-access-permits', label: 'Mall Access Permits', type: 'description', indented: true },
  {
    id: 'mewp-specifications-2',
    label: 'Mobile elevating work platform Specifications',
    type: 'description',
    indented: true,
  },
  { id: 'tools', label: 'Tools', type: 'description', indented: true },
  { id: 'delivery-details', label: 'Delivery details', type: 'description', indented: true },
];

const SUPPLIER_EMAIL_BODY_ITEMS: JourneySubStep[] = [
  { id: 'order-name-number', label: 'Order name and number', type: 'description', indented: true },
  { id: 'indoor', label: 'Indoor', type: 'description', indented: true },
  { id: 'outdoor', label: 'Outdoor', type: 'description', indented: true },
  { id: 'device-name', label: 'Device name', type: 'description', indented: true },
  { id: 'location', label: 'Location', type: 'description', indented: true },
  { id: 'material', label: 'Material', type: 'description', indented: true },
  { id: 'sqm-total', label: 'SQM, square meter (total)', type: 'description', indented: true },
  { id: 'artwork-preview', label: 'Artwork Preview', type: 'description', indented: true },
  {
    id: 'illumination-requirements',
    label: 'Illumination requirements',
    type: 'description',
    indented: true,
  },
  { id: 'power-supply', label: 'Power supply required', type: 'description', indented: true },
  { id: 'mall-access-permits', label: 'Mall Access Permits', type: 'description', indented: true },
  {
    id: 'team-document-id',
    label: 'Team identity document ID',
    type: 'description',
    indented: true,
  },
  {
    id: 'mewp-specifications',
    label: 'Mobile elevating work platform Specifications',
    type: 'description',
    indented: true,
  },
  { id: 'tools', label: 'Tools', type: 'description', indented: true },
  { id: 'job-date', label: 'Job Date', type: 'description', indented: true },
  { id: 'delivery-details', label: 'Delivery details', type: 'description', indented: true },
];

const LOCATION_INSTALLATION_EMAIL_BODY_ITEMS: JourneySubStep[] = [
  { id: 'order-name-number', label: 'Order name and number', type: 'description', indented: true },
  { id: 'date-and-time', label: 'Date and Time', type: 'description', indented: true },
  { id: 'location', label: 'Location', type: 'description', indented: true },
  { id: 'outdoor', label: 'Outdoor', type: 'description', indented: true },
  { id: 'device-name', label: 'Device name', type: 'description', indented: true },
  { id: 'height-permits', label: 'Height Permits', type: 'description', indented: true },
  {
    id: 'ssm-industrial-climber',
    label: 'SSM Industrial climber',
    type: 'description',
    indented: true,
  },
  {
    id: 'mewp-specifications',
    label: 'Mobile elevating work platform Specifications',
    type: 'description',
    indented: true,
  },
  {
    id: 'mewp-iscir',
    label: 'Mobile elevating work platform ISCIR',
    type: 'description',
    indented: true,
  },
  {
    id: 'mewp-operator',
    label: 'Mobile elevating work platform Operator',
    type: 'description',
    indented: true,
  },
  {
    id: 'vehicle-plate-id',
    label: 'Vehicle licence plate ID',
    type: 'description',
    indented: true,
  },
  {
    id: 'location-security-phone',
    label: 'Location security/dispatch telephone number for access',
    type: 'description',
    indented: true,
  },
  {
    id: 'estimated-duration',
    label: 'Estimated duration of the operation',
    type: 'description',
    indented: true,
  },
  {
    id: 'team-document-id',
    label: 'Team identity document ID',
    type: 'description',
    indented: true,
  },
  { id: 'material', label: 'Material', type: 'description', indented: true },
  { id: 'dimensions', label: 'Dimensions', type: 'description', indented: true },
  { id: 'artwork-preview', label: 'Artwork Preview', type: 'description', indented: true },
  {
    id: 'illumination-requirements',
    label: 'Illumination requirements',
    type: 'description',
    indented: true,
  },
  { id: 'power-supply', label: 'Power supply required', type: 'description', indented: true },
  { id: 'mall-access-permits', label: 'Mall Access Permits', type: 'description', indented: true },
  {
    id: 'mewp-specifications-2',
    label: 'Mobile elevating work platform Specifications',
    type: 'description',
    indented: true,
  },
  { id: 'tools', label: 'Tools', type: 'description', indented: true },
  { id: 'delivery-details', label: 'Delivery details', type: 'description', indented: true },
];

export const JOURNEY_STEPS_CONFIG: JourneyStepConfig[] = [
  {
    id: 'print',
    title: 'Print',
    actions: [
      {
        id: 'print-planning',
        title: 'Print Planning',
        source: 'api',
        subSteps: [
          {
            id: 'ASSIGN_ORDER',
            label: 'Assign the order (or part of the order) to a team member.',
          },
          { id: 'ASSIGN_RESPONSIBLE', label: 'Assign a Print Proof responsible person.' },
          { id: 'ARTWORK_RECEIVED', label: 'Artwork Received. (Artwork Link or Attachment)' },
          { id: 'SEND_ARTWORK_PREVIEW', label: 'Send the Artwork Preview for approval.' },
          {
            id: 'ARTWORK_PREVIEW_APPROVED',
            label: 'Artwork Preview Approved',
            rejectedLabel: 'Artwork Preview Rejected',
          },
        ],
      },
      {
        id: 'dtp-check',
        title: 'DTP Check',
        source: 'manual',
        subSteps: [
          {
            id: 'open-device-sketch',
            label: 'Open the advertising device sketch.',
            type: 'description',
          },
          { id: 'download-artwork', label: 'Download Artwork.', type: 'description' },
          {
            id: 'artwork-printable-format',
            label: 'Artwork printable format',
            type: 'description',
          },
          { id: 'artwork-error-check', label: 'Artwork error check.', type: 'description' },
          { id: 'artwork-size-check', label: 'Artwork size check.', type: 'description' },
          { id: 'artwork-color-check', label: 'Artwork color check', type: 'description' },
          { id: 'material-type-check', label: 'Material Type check', type: 'description' },
          { id: 'resolution-check', label: 'Resolution check', type: 'description' },
          {
            id: 'export-artwork',
            label: 'Export artwork for Printing Supplier',
            type: 'description',
          },
          { id: 'sqm-total', label: 'SQM, square meter (total)', type: 'description' },
          {
            id: 'generate-export-link',
            label: 'Generate artwork export link for Printing Supplier',
            type: 'description',
          },
          {
            id: 'export-sketch-preview',
            label: 'Export sketch artwork preview for Deployment and Printing Supplier',
            type: 'description',
          },
        ],
      },
      {
        id: 'send-email-order',
        title: 'Send Email order to print supplier',
        source: 'manual',
        emailTemplate: {
          kind: 'print-supplier',
          fileSections: ['artwork-previews', 'support-pictures'],
        },
        subSteps: [
          { id: 'email-order', label: 'Email order to print supplier', type: 'checkbox' },
          {
            id: 'job-name-number',
            label: 'Job name and number',
            type: 'description',
            indented: true,
          },
          { id: 'device-name', label: 'Device name', type: 'description', indented: true },
          { id: 'location', label: 'Location', type: 'description', indented: true },
          { id: 'material', label: 'Material', type: 'description', indented: true },
          { id: 'print-type', label: 'Print type', type: 'description', indented: true },
          { id: 'dimensions', label: 'Dimensions', type: 'description', indented: true },
          {
            id: 'sqm-total',
            label: 'SQM, square meter (total)',
            type: 'description',
            indented: true,
          },
          { id: 'quantity', label: 'Quantity', type: 'description', indented: true },
          { id: 'finishing', label: 'Finishing', type: 'description', indented: true },
          {
            id: 'simulation',
            label: 'Simulation (if exists)',
            type: 'description',
            indented: true,
          },
          { id: 'shipping-info', label: 'Shipping info', type: 'description', indented: true },
          { id: 'packaging', label: 'Packaging', type: 'description', indented: true },
          { id: 'artwork-preview', label: 'Artwork Preview', type: 'description', indented: true },
          { id: 'cost', label: 'Cost', type: 'description', indented: true },
          { id: 'artwork', label: 'Artwork', type: 'description', indented: true },
          { id: 'due-date', label: 'Due Date', type: 'description', indented: true },
          {
            id: 'supplier-acceptance',
            label: 'Supplier Acceptance',
            type: 'accept-reject',
            acceptSubStepId: 'email-order',
          },
          { id: 'follow-up-due-date', label: 'Follow up order Due Date', type: 'checkbox' },
        ],
      },
    ],
  },
  {
    id: 'decoration',
    title: 'Decoration',
    actions: [
      {
        id: 'email-to-location',
        title: 'Email to Location',
        source: 'manual',
        emailTemplate: {
          kind: 'location-decoration',
          fileSections: ['artwork-previews', 'support-pictures'],
        },
        subSteps: [
          {
            id: 'write-email-location',
            label: 'Write the email to Location using the email body',
            type: 'checkbox',
          },
          ...LOCATION_EMAIL_BODY_ITEMS.map((item) => ({ ...item, id: `loc-email-1-${item.id}` })),
          {
            id: 'location-approved',
            label: 'Location has to answer to email with Approved',
            type: 'checkbox',
          },
        ],
      },
      {
        id: 'email-to-supplier',
        title: 'Email to Supplier',
        source: 'manual',
        emailTemplate: {
          kind: 'supplier-decoration',
          fileSections: ['simulations', 'support-pictures'],
        },
        subSteps: [
          {
            id: 'write-email-supplier',
            label: 'Write the email to supplier using the email body',
            type: 'checkbox',
          },
          {
            id: 'order-name-number',
            label: 'Order name and number',
            type: 'description',
            indented: true,
          },
          { id: 'indoor', label: 'Indoor', type: 'description', indented: true },
          { id: 'outdoor', label: 'Outdoor', type: 'description', indented: true },
          { id: 'device-name', label: 'Device name', type: 'description', indented: true },
          { id: 'location', label: 'Location', type: 'description', indented: true },
          { id: 'material', label: 'Material', type: 'description', indented: true },
          {
            id: 'dimensions-sqm',
            label: 'Dimensions SQM, square meter (total)',
            type: 'description',
            indented: true,
          },
          { id: 'artwork-preview', label: 'Artwork Preview', type: 'description', indented: true },
          {
            id: 'illumination-requirements',
            label: 'Illumination requirements',
            type: 'description',
            indented: true,
          },
          {
            id: 'power-supply',
            label: 'Power supply required',
            type: 'description',
            indented: true,
          },
          {
            id: 'mall-access-permits',
            label: 'Mall Access Permits',
            type: 'description',
            indented: true,
          },
          {
            id: 'team-document-id',
            label: 'Team identity document ID',
            type: 'description',
            indented: true,
          },
          { id: 'ladder', label: 'Ladder', type: 'description', indented: true },
          { id: 'tools', label: 'Tools', type: 'description', indented: true },
          { id: 'job-date', label: 'Job Date', type: 'description', indented: true },
          {
            id: 'supplier-approved',
            label: 'Supplier has to answer to email with Approved',
            type: 'checkbox',
          },
        ],
      },
      {
        id: 'mark-delivery-method',
        title: 'Mark delivery method',
        source: 'manual',
        subSteps: [
          {
            id: 'delivery-method',
            label: 'Delivery Method',
            type: 'radio',
            options: ['Courier', 'UBER/BOLT', 'Personal Delivery'],
          },
          { id: 'job-name-number', label: 'Job name and number', type: 'description' },
          {
            id: 'sender-destination',
            label: 'Sender and Destination contact and address',
            type: 'description',
          },
          { id: 'delivery-time-window', label: 'Delivery time window', type: 'description' },
          { id: 'packages', label: 'Packages', type: 'description' },
          { id: 'awb', label: 'AWB', type: 'description' },
          { id: 'verify-packages', label: 'Verify packages', type: 'description' },
          {
            id: 'integrity-check',
            label: 'Integrity check at package reception (Upload photo)',
            type: 'description',
          },
          { id: 'special-delivery', label: 'Special Delivery Instructions', type: 'description' },
          {
            id: 'reverse-logistics',
            label: 'Reverse logistics / Return of old materials',
            type: 'description',
          },
          {
            id: 'estimated-transport-cost',
            label: 'Estimated transport cost',
            type: 'description',
          },
          { id: 'internal-handover', label: 'Internal handover signature', type: 'description' },
          { id: 'failed-delivery', label: 'Failed delivery attempt protocol', type: 'description' },
          { id: 'inform-supplier', label: 'Inform Supplier', type: 'checkbox' },
          {
            id: 'uber-plate-driver',
            label: 'Uber / personal plate number and Driver Name',
            type: 'checkbox',
          },
        ],
      },
      {
        id: 'contact-supplier',
        title: 'Contact supplier and inform about: ORDER',
        source: 'manual',
        emailTemplate: {
          kind: 'supplier-order-update',
          fileSections: ['simulations', 'support-pictures'],
        },
        subSteps: [
          {
            id: 'write-email-supplier',
            label: 'Write the email to supplier using the email body',
            type: 'checkbox',
          },
          ...SUPPLIER_EMAIL_BODY_ITEMS.map((item) => ({ ...item, id: `sup-email-${item.id}` })),
          {
            id: 'pre-installation-photos',
            label: 'Pre-installation photos',
            type: 'description',
            indented: true,
          },
          {
            id: 'supplier-approved',
            label: 'Supplier has to answer to email with Approved',
            type: 'checkbox',
          },
        ],
      },
      {
        id: 'print-delivered',
        title: 'Print Delivered',
        source: 'manual',
        subSteps: [],
      },
      {
        id: 'email-to-location-installation',
        title: 'Email to Location decoration',
        source: 'manual',
        emailTemplate: {
          kind: 'location-installation',
          fileSections: ['support-pictures', 'simulations', 'artwork-previews'],
        },
        subSteps: [
          {
            id: 'write-email-location',
            label: 'Write the email to Location using the email body',
            type: 'checkbox',
          },
          ...LOCATION_INSTALLATION_EMAIL_BODY_ITEMS.map((item) => ({
            ...item,
            id: `loc-email-2-${item.id}`,
          })),
          { id: 'location-approved', label: 'Location has Approved', type: 'checkbox' },
        ],
      },
      {
        id: 'decoration-proof',
        title: 'Decoration Proof',
        source: 'manual',
        subSteps: [
          {
            id: 'request-confirmation',
            label: 'Request confirmation of Decoration (Email / Mobile)',
            type: 'checkbox',
          },
          { id: 'confirm-decoration', label: 'Confirm Decoration', type: 'checkbox' },
          { id: 'upload-decoration-photos', label: 'Upload Decoration photos', type: 'checkbox' },
          {
            id: 'request-daytime-photos',
            label: 'Request daytime Decoration photos (optional)',
            type: 'checkbox',
          },
          {
            id: 'upload-daytime-photos',
            label: 'Upload daytime Decoration photos (optional)',
            type: 'checkbox',
          },
          {
            id: 'send-redecoration-email',
            label:
              'Send email to Decoration supplier about the issue to be fixed. (if redecoration is necessary)',
            type: 'checkbox',
          },
          { id: 'redecoration-done', label: 'Redecoration is done', type: 'checkbox' },
          { id: 'upload-photo', label: 'Upload photo', type: 'checkbox' },
        ],
      },
    ],
  },
  {
    id: 'neutralization',
    title: 'Neutralization',
    actions: [
      {
        id: 'email-to-location-neutralization',
        title: 'Email to Location neutralization',
        source: 'manual',
        emailTemplate: {
          kind: 'location-neutralization',
          fileSections: ['support-pictures', 'after-decoration'],
        },
        subSteps: [
          {
            id: 'write-email-location',
            label: 'Write the email to Location using the email body',
            type: 'checkbox',
          },
          ...LOCATION_EMAIL_BODY_ITEMS.map((item) => ({ ...item, id: `loc-neut-${item.id}` })),
          { id: 'location-approved', label: 'Location has Approved', type: 'checkbox' },
        ],
      },
      {
        id: 'contact-supplier-neutralization',
        title: 'Contact supplier and inform about: ORDER',
        source: 'manual',
        emailTemplate: {
          kind: 'supplier-neutralization',
          fileSections: ['support-pictures', 'after-decoration'],
        },
        subSteps: [
          {
            id: 'write-email-supplier',
            label: 'Write the email to supplier using the email body',
            type: 'checkbox',
          },
          ...SUPPLIER_EMAIL_BODY_ITEMS.map((item) => ({ ...item, id: `sup-neut-${item.id}` })),
          {
            id: 'pre-neutralization-photos',
            label: 'Pre-neutralization photos',
            type: 'description',
            indented: true,
          },
          {
            id: 'supplier-approved',
            label: 'Supplier has to answer to email with Approved',
            type: 'checkbox',
          },
        ],
      },
      {
        id: 'neutralization-proof',
        title: 'Neutralization Proof',
        source: 'manual',
        subSteps: [
          {
            id: 'request-confirmation',
            label: 'Request confirmation of Neutralization (Email / Mobile)',
            type: 'checkbox',
          },
          { id: 'confirm-neutralization', label: 'Confirm Neutralization', type: 'checkbox' },
          {
            id: 'upload-neutralization-photos',
            label: 'Upload Neutralization photos',
            type: 'checkbox',
          },
        ],
      },
    ],
  },
];

/** Canonical mapping between a service-task serviceType and its journey step id (and the inverse). */
export const SERVICE_TYPE_TO_STEP: Record<string, string> = {
  PRINT: 'print',
  DECORATION: 'decoration',
  NEUTRALIZATION: 'neutralization',
};

export const STEP_TO_SERVICE_TYPE: Record<string, string> = {
  print: 'PRINT',
  decoration: 'DECORATION',
  neutralization: 'NEUTRALIZATION',
};

function findJourneyTitle(id: string | null | undefined): string | null {
  if (!id) return null;
  for (const step of JOURNEY_STEPS_CONFIG) {
    for (const action of step.actions) {
      if (action.id === id) return action.title;
      const subStep = action.subSteps.find((s) => s.id === id);
      if (subStep) return subStep.label;
    }
    if (step.id === id) return step.title;
  }
  return null;
}

export function getJourneyStepLabel(
  t: TFunction,
  stepId?: string | null,
  substepId?: string | null,
): string | null {
  const title = findJourneyTitle(substepId) ?? findJourneyTitle(stepId);
  if (!title) return null;
  return t(title.replace(/\.$/, ''));
}

export function getRejectedSubStepIds(
  progressData: JourneyProgressResponse | undefined,
  stepId: string,
  actionId: string,
): string[] {
  if (!progressData) return [];
  if (actionId === 'print-planning' && progressData.artworkStatus === 'REJECTED') {
    return ['ARTWORK_PREVIEW_APPROVED'];
  }
  return [];
}

export function getCompletedSubStepIds(
  progressData: JourneyProgressResponse | undefined,
  stepId: string,
  actionId: string,
): string[] {
  if (!progressData) return [];

  if (actionId === 'print-planning') {
    const completed: string[] = [];
    if (progressData.responsibleSet) {
      completed.push('ASSIGN_ORDER', 'ASSIGN_RESPONSIBLE');
    }
    if (progressData.artworkUploaded) completed.push('ARTWORK_RECEIVED');
    if (progressData.artworkPreviewUploaded) completed.push('SEND_ARTWORK_PREVIEW');
    if (progressData.artworkStatus === 'APPROVED') completed.push('ARTWORK_PREVIEW_APPROVED');
    return completed;
  }

  // `steps` is declared non-optional in the generated contract, so TypeScript
  // offers no warning here — but a payload reaching this helper without one used
  // to throw and take the whole page down through the error boundary. Degrade to
  // "nothing completed" instead.
  const stepProgress = progressData.steps?.find((s) => s.stepId === stepId);
  if (!stepProgress) return [];
  return Object.entries(stepProgress.substeps)
    .filter(([, completed]) => completed)
    .map(([id]) => id);
}

export function isStepActionCompleted(
  progressData: JourneyProgressResponse | undefined,
  stepId: string,
  actionId: string,
): boolean {
  // `?.` on progressData alone is not enough — see the note in
  // getCompletedSubStepIds; `steps` itself can be absent.
  const stepProgress = progressData?.steps?.find((s) => s.stepId === stepId);
  if (stepProgress && actionId in stepProgress.substeps) return stepProgress.substeps[actionId];

  const step = JOURNEY_STEPS_CONFIG.find((s) => s.id === stepId);
  const action = step?.actions.find((a) => a.id === actionId);
  if (!action || action.subSteps.length === 0) return stepProgress?.completed ?? false;

  const completedIds = getCompletedSubStepIds(progressData, stepId, actionId);
  return action.subSteps.every((s) => completedIds.includes(s.id));
}
